import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  CONTENT_RATINGS,
  DEFAULT_PREFERENCES,
  TIERS,
  type UserPreferences,
} from '@gobingebox/shared';
import { cn } from '@/lib/cn';
import { PageHero } from '@/components/PagePlaceholder';
import { Alert } from '@/components/ui/Alert';
import { Button, buttonVariants } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Check, LogOut } from '@/components/icons';
import { meApi } from '@/features/catalog/api';
import { authApi } from '@/features/auth/api';
import { billingApi } from '@/features/billing/api';
import { ProfilesManager } from '@/features/profiles/ProfilesManager';
import { useAuth, useRefreshViewer } from '@/features/auth/AuthContext';

type Section = 'general' | 'profiles' | 'preferences' | 'account' | 'purchase';
const SECTIONS: { id: Section; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'profiles', label: 'Profiles' },
  { id: 'preferences', label: 'Preferences' },
  { id: 'account', label: 'Account' },
  { id: 'purchase', label: 'Purchase & plan' },
];

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-wrap items-center justify-between gap-3 border-b border-border py-3.5 last:border-0">
      <span className="text-sm text-ink-muted">{label}</span>
      {children}
    </label>
  );
}

function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-lg border border-border bg-surface-raised px-2 text-sm text-ink"
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>{label}</option>
      ))}
    </select>
  );
}

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onClick}
      className={cn('relative h-6 w-11 rounded-full transition-colors', on ? 'bg-brand' : 'bg-surface-raised')}
    >
      <span className={cn('absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all', on ? 'left-[22px]' : 'left-0.5')} />
    </button>
  );
}

export function AccountPage() {
  const { user, viewer } = useAuth();
  const refresh = useRefreshViewer();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialSection = (SECTIONS.find((s) => s.id === params.get('section'))?.id ?? 'general') as Section;
  const [section, setSection] = useState<Section>(initialSection);
  const [name, setName] = useState(user?.displayName ?? '');
  const [prefs, setPrefs] = useState<UserPreferences>(user?.preferences ?? DEFAULT_PREFERENCES);
  const [portalBusy, setPortalBusy] = useState(false);
  const [resent, setResent] = useState(false);

  const saveName = useMutation({
    mutationFn: () => meApi.updateProfile(name.trim()),
    onSuccess: () => refresh(),
  });
  const savePrefs = useMutation({
    mutationFn: () => meApi.updatePreferences(prefs),
    onSuccess: () => refresh(),
  });

  if (!user) return null;
  const sub = viewer?.subscription ?? null;

  async function manageBilling() {
    setPortalBusy(true);
    try {
      const { url } = await billingApi.portal();
      window.location.href = url;
    } catch {
      setPortalBusy(false);
    }
  }
  async function signOut() {
    await authApi.logout().catch(() => undefined);
    await refresh();
    navigate('/');
  }

  const setPref = <K extends keyof UserPreferences>(k: K, v: UserPreferences[K]) =>
    setPrefs((p) => ({ ...p, [k]: v }));

  return (
    <div>
      <PageHero eyebrow="Account" title="Settings" />
      <div className="container-page grid gap-8 pb-16 lg:grid-cols-[200px_1fr]">
        <nav className="flex gap-2 overflow-x-auto lg:flex-col">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSection(s.id)}
              className={cn(
                'shrink-0 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors',
                section === s.id ? 'bg-surface-raised text-ink' : 'text-ink-muted hover:text-ink',
              )}
            >
              {s.label}
            </button>
          ))}
        </nav>

        <div className="max-w-xl">
          {section === 'general' && (
            <section className="card p-6">
              <h2 className="font-display text-lg font-bold text-ink">Profile</h2>
              <div className="mt-4 space-y-4">
                <Field label="Display name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
                <p className="text-xs text-ink-faint">Member since {new Date(user.createdAt).toLocaleDateString()}</p>
                <Button onClick={() => saveName.mutate()} disabled={saveName.isPending || !name.trim()}>
                  {saveName.isSuccess ? 'Saved' : saveName.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </section>
          )}

          {section === 'profiles' && (
            <section>
              <h2 className="mb-4 font-display text-lg font-bold text-ink">Profiles</h2>
              <ProfilesManager />
            </section>
          )}

          {section === 'preferences' && (
            <section className="card p-6">
              <h2 className="font-display text-lg font-bold text-ink">Preferences</h2>
              <p className="mt-1 text-sm text-ink-muted">Set your language and playback defaults.</p>
              <div className="mt-3">
                <Row label="Display language">
                  <Select value={prefs.displayLanguage} onChange={(v) => setPref('displayLanguage', v as UserPreferences['displayLanguage'])} options={[['en', 'English'], ['ja', '日本語']]} />
                </Row>
                <Row label="Audio language">
                  <Select value={prefs.audioLanguage} onChange={(v) => setPref('audioLanguage', v as UserPreferences['audioLanguage'])} options={[['ja', 'Japanese'], ['en', 'English']]} />
                </Row>
                <Row label="Subtitles / CC language">
                  <Select value={prefs.subtitleLanguage} onChange={(v) => setPref('subtitleLanguage', v as UserPreferences['subtitleLanguage'])} options={[['off', 'Off'], ['en', 'English'], ['ja', 'Japanese']]} />
                </Row>
                <Row label="Closed captions">
                  <Toggle on={prefs.closedCaptions} onClick={() => setPref('closedCaptions', !prefs.closedCaptions)} />
                </Row>
                <Row label="Audio descriptions">
                  <Toggle on={prefs.audioDescriptions} onClick={() => setPref('audioDescriptions', !prefs.audioDescriptions)} />
                </Row>
                <Row label="Content restriction">
                  <Select
                    value={prefs.contentRestriction}
                    onChange={(v) => setPref('contentRestriction', v as UserPreferences['contentRestriction'])}
                    options={[['none', 'No limit'], ...CONTENT_RATINGS.map((r) => [r.value, `Up to ${r.label}`] as [string, string])]}
                  />
                </Row>
              </div>
              <Button className="mt-5" onClick={() => savePrefs.mutate()} disabled={savePrefs.isPending}>
                {savePrefs.isSuccess ? 'Saved' : savePrefs.isPending ? 'Saving…' : 'Save preferences'}
              </Button>
            </section>
          )}

          {section === 'account' && (
            <section className="card p-6">
              <h2 className="font-display text-lg font-bold text-ink">Account</h2>
              <div className="mt-3">
                <Row label="Email"><span className="text-sm font-medium text-ink">{user.email}</span></Row>
                <Row label="Email verified">
                  {user.emailVerified ? (
                    <span className="inline-flex items-center gap-1.5 text-sm text-success"><Check size={15} /> Verified</span>
                  ) : resent ? (
                    <span className="text-sm text-ink-muted">Verification sent</span>
                  ) : (
                    <button type="button" onClick={() => { void authApi.resendVerification(); setResent(true); }} className="text-sm font-semibold text-brand-soft">
                      Resend verification
                    </button>
                  )}
                </Row>
                <Row label="Password"><Link to="/forgot-password" className="text-sm font-semibold text-brand-soft">Change password</Link></Row>
              </div>
              <Button variant="outline" className="mt-5" onClick={signOut}><LogOut size={16} /> Sign out</Button>
            </section>
          )}

          {section === 'purchase' && (
            <section className="card p-6">
              <h2 className="font-display text-lg font-bold text-ink">Purchase &amp; plan</h2>
              <div className="mt-3 text-sm text-ink-muted">
                {user.role === 'owner' || user.role === 'admin' || user.role === 'contributor' ? (
                  <Alert tone="info">You have full studio access — no subscription needed.</Alert>
                ) : sub ? (
                  <div className="space-y-1.5">
                    <p className="text-ink">
                      <span className="font-semibold">{TIERS[sub.tier].name}</span>
                      {sub.source === 'comp' && <span className="ml-2 rounded-full bg-success/15 px-2 py-0.5 text-2xs font-semibold uppercase text-success">Comp · free</span>}
                    </p>
                    <p>{sub.streams} stream{sub.streams > 1 ? 's' : ''} · {sub.source === 'comp' ? 'granted by the studio' : sub.billingPeriod === 'annual' ? 'annual plan' : 'monthly plan'}</p>
                  </div>
                ) : (
                  'No active plan — you can browse and watch previews. Subscribe to unlock full episodes.'
                )}
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <Link to="/plans" className={buttonVariants({ variant: 'secondary', size: 'md' })}>View plans</Link>
                {sub?.source === 'stripe' && (
                  <Button variant="outline" onClick={manageBilling} disabled={portalBusy}>
                    {portalBusy ? 'Opening…' : 'Manage billing'}
                  </Button>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
